"use client";

import { CircularProgress } from "@mui/material";
import Grid from "@mui/material/Grid2";
import React, { useEffect } from "react";

import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import PageContainer from "@/components/atoms/PageContainer";
import PageLayout from "@/components/templates/PageLayout";
import ROUTE_URLS from "@/constants/route-urls";
import useLanguage from "@/contexts/language-context";
import useGetAllPlatforms from "@/hooks/requests/platform/use-get-all-platform";
import { usePlatformId } from "@/hooks/requests/platform/use-platform-id";
import { IPlatform } from "@/interfaces/platform.interfaces";
import { PlatformsMessages } from "@/messages/platforms/platforms.messages";
import { BreadCrumb } from "@/types/page.types";

import DefaultPlatformCard from "./DefaultPlatformCard";

type Props = {
  messages: PlatformsMessages;
};

function PlatformsPage({ messages }: Props) {
  const { lang } = useLanguage();
  const { data: platformsResponse, isLoading } = useGetAllPlatforms();
  const { updatePlatforms } = usePlatformId();

  // Atualizar as plataformas no cache do SWR quando carregadas
  useEffect(() => {
    if (platformsResponse?.items) {
      updatePlatforms(platformsResponse.items);
    }
  }, [platformsResponse, updatePlatforms]);

  const platforms = platformsResponse?.items || [];

  const groupedPlatforms: Record<string, IPlatform[]> = {};

  platforms?.forEach((platform) => {
    // Mapear tipo numérico para string da categoria
    let categoryKey: string;
    switch (platform.type) {
      case 1:
        categoryKey = "communication";
        break;
      case 2:
        categoryKey = "e-commerce";
        break;
      case 3:
        categoryKey = "erp";
        break;
      case 4:
        categoryKey = "payment";
        break;
      case 5:
        categoryKey = "logistics";
        break;
      case 6:
        categoryKey = "attendance";
        break;
      default:
        categoryKey = "other";
    }

    if (!groupedPlatforms[categoryKey]) {
      groupedPlatforms[categoryKey] = [];
    }
    groupedPlatforms[categoryKey].push(platform);
  });

  const BREAD_CRUMBS: BreadCrumb[] = [
    { title: messages.platformsPageTitle, href: ROUTE_URLS.platform(lang) },
  ];

  if (isLoading) {
    return (
      <PageLayout messages={messages} breadCrumbs={BREAD_CRUMBS}>
        <PageContainer>
          <MDBox display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </MDBox>
        </PageContainer>
      </PageLayout>
    );
  }

  // Se não temos plataformas, mostrar uma mensagem
  if (!platforms || platforms.length === 0) {
    return (
      <PageLayout messages={messages} breadCrumbs={BREAD_CRUMBS}>
        <PageContainer>
          <MDTypography variant="h6">Nenhuma plataforma encontrada.</MDTypography>
        </PageContainer>
      </PageLayout>
    );
  }

  return (
    <PageLayout messages={messages} breadCrumbs={BREAD_CRUMBS}>
      <PageContainer>
        {Object.entries(groupedPlatforms).length === 0 && (
          <MDTypography variant="h6" mb={2}>
            {platforms.length > 0
              ? `${platforms.length} plataforma(s) encontrada(s) mas sem categoria válida.`
              : "Nenhuma plataforma encontrada."}
          </MDTypography>
        )}

        {Object.entries(groupedPlatforms).map(([category, categoryPlatforms]) => (
          <MDBox key={category} mb={4}>
            <MDTypography variant="h5" gutterBottom fontWeight="bold" my={2}>
              {messages[category as keyof PlatformsMessages] || category}
            </MDTypography>

            <Grid container spacing={3}>
              {categoryPlatforms.map((platform) => (
                <Grid
                  size={{ xs: 12, sm: 6, md: 4, lg: 3 }}
                  key={platform.id}
                  sx={{ display: "flex" }}
                >
                  <DefaultPlatformCard platform={platform} messages={messages} />
                </Grid>
              ))}
            </Grid>
          </MDBox>
        ))}
      </PageContainer>
    </PageLayout>
  );
}

export default PlatformsPage;
